// store.ts — typed front-end wrappers over the on-device Rust SQLite commands (local-first store).
//
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Research history NEVER
// leaves the device. The ONLY thing that goes to the brain is the user's question + each step's
// intermediate text (see brain.ts, billed BYOK). Saved briefs + "search past research" are answered
// entirely on-device here, no brain call.
//
// Data model:
//   brief  { id, question, status, summary, source_count, elapsed_ms, created_at, updated_at }
//          + body (the full structured brief: summary + key_points[] + sources[])
//   source { ordinal, title, url, domain, snippet, photo_ref }  ← the brief's sources, in citation
//          order. photo_ref set = a PHOTOGRAPHED source the user attached (on-device JPEG data-URL).

import { invoke } from "./tauri-web";

export type BriefStatus = "running" | "done" | "failed";

/** One cited source. `ordinal` is the 1-based citation number the key points reference ("[3]"). */
export interface Source {
  ordinal: number;
  title: string;
  url: string;
  /** Clean host label, derived ON-DEVICE in Rust (e.g. "en.wikipedia.org"). "" for a photo source. */
  domain: string;
  snippet: string;
  /** A PHOTOGRAPHED source the user attached (page/report/whiteboard/slide) — a downscaled JPEG
   *  data-URL, ON-DEVICE only. "" for an ordinary web-cited source. When set, `url`/`domain` are
   *  empty. Never left undefined (the web build reads it). Sent to the brain only as image input
   *  for the synthesize step that wrote the brief; never otherwise. */
  photo_ref: string;
}

/** One key point of a brief — a claim grounded in one or more cited sources. */
export interface KeyPoint {
  text: string;
  /** 1-based ordinals into the brief's `sources`. */
  source_ordinals: number[];
}

/** The structured brief shape stored verbatim in a brief's body_json (server contract). */
export interface BriefBody {
  summary: string;
  key_points: KeyPoint[];
  sources: Source[];
}

/** History list row (no body — keeps the list payload tiny). */
export interface BriefMeta {
  id: string;
  question: string;
  /** The customer's OWN label. Empty = show the question. Never replaces `question`. */
  title: string;
  status: BriefStatus;
  summary: string;
  source_count: number;
  /** How long the research loop took, ms. */
  elapsed_ms: number;
  created_at: string;
  updated_at: string;
}

/** A full saved brief (history detail). */
export interface Brief extends BriefMeta {
  /** The full structured brief JSON as the server returned it (stays on-device). */
  body_json: string;
  /** The customer's OWN notes — what they made of this brief. */
  notes: string;
}

/** The completion payload persisted at the end of a successful research loop. */
export interface CompleteBriefInput {
  id: string;
  summary: string;
  body_json: string;
  sources: { ordinal: number; title: string; url: string; snippet?: string; photo_ref?: string }[];
  /** Flattened key-point text for the local FTS index ("search past research"). */
  key_points_text: string;
  elapsed_ms: number;
}

export const store = {
  /** Liveness check against the native side — returns "pong". */
  ping: () => invoke<string>("ping"),

  // ── research history (local-first) ──
  /** Open a new research run (status `running`) and get its id. Persisted before the loop starts. */
  beginBrief: (question: string) => invoke<string>("begin_brief", { question }),
  /** Finish a run: store the synthesized brief + its sources, flip to `done`, index it locally. */
  completeBrief: (input: CompleteBriefInput) => invoke<Brief | null>("complete_brief", { input }),
  /** Mark a run as failed (a step errored) — kept in history, retryable. */
  failBrief: (id: string) => invoke<void>("fail_brief", { id }),
  listBriefs: (limit = 100) => invoke<BriefMeta[]>("list_briefs", { limit }),
  getBrief: (id: string) => invoke<Brief | null>("get_brief", { id }),
  sourcesFor: (id: string) => invoke<Source[]>("sources_for_brief", { id }),
  /** Local FTS5 history search (no brain call). Empty query → newest briefs. */
  searchHistory: (query: string, limit = 100) => invoke<BriefMeta[]>("search_history", { query, limit }),
  briefCount: () => invoke<number>("brief_count"),
  deleteBrief: (id: string) => invoke<void>("delete_brief", { id }),
  /** Edit the customer's own label/notes. Omit a field to leave it as it is. */
  updateBrief: (id: string, patch: { title?: string; notes?: string }) =>
    invoke<void>("update_brief", { id, title: patch.title ?? null, notes: patch.notes ?? null }),
  /** Wipe all on-device research (clear history). */
  clearHistory: () => invoke<void>("clear_history"),
};

// ── Pure presentation helpers ──────────────────────────────────────────────────

/** Parse a stored brief's body_json into the structured shape (defensive — never throws). */
export function parseBriefBody(b: Brief | null): BriefBody | null {
  if (!b) return null;
  try {
    const raw = JSON.parse(b.body_json) as Partial<BriefBody>;
    return {
      summary: typeof raw.summary === "string" ? raw.summary : b.summary,
      key_points: Array.isArray(raw.key_points)
        ? raw.key_points
            .filter((k): k is KeyPoint => !!k && typeof k.text === "string")
            .map((k) => ({ text: k.text, source_ordinals: Array.isArray(k.source_ordinals) ? k.source_ordinals : [] }))
        : [],
      sources: Array.isArray(raw.sources) ? raw.sources : [],
    };
  } catch {
    return { summary: b.summary, key_points: [], sources: [] };
  }
}

/** Pretty "8.2s" / "1m 04s" for a research duration. */
export function fmtElapsed(ms: number): string {
  if (ms <= 0) return "";
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return `${m}m ${rem.toString().padStart(2, "0")}s`;
}

/** Relative "just now" / "3h ago" / "2 Jun" for a brief's created_at. */
export function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
}
